import { useEffect, useRef, useState } from "react";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
  useScroll,
  useTransform,
} from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";

gsap.registerPlugin(ScrollTrigger);

// ---------------------------------------------------------------------
// Shared easing so every "story beat" (reveal, split-text, loader) uses
// the same curve — it's what makes the separate effects read as one
// coherent motion language instead of a pile of unrelated tricks.
// ---------------------------------------------------------------------
export const EASE = [0.22, 1, 0.36, 1];

// ---------------------------------------------------------------------
// 1) SMOOTH SCROLLING
// Turns on native smooth scrolling site-wide. Mount once near the root
// (we do it in Navbar, since it's present on every page).
// ---------------------------------------------------------------------
export function useSmoothScroll() {
  useEffect(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduceMotion) {
      const prev = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = "smooth";
      return () => { document.documentElement.style.scrollBehavior = prev; };
    }

    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });

    lenis.on("scroll", ScrollTrigger.update);

    const syncTicker = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(syncTicker);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(syncTicker);
      lenis.destroy();
    };
  }, []);
}

// ---------------------------------------------------------------------
// 2) SCROLL REVEAL / SECTION TRANSITIONS
// Wrap a whole <section>'s content in this so it enters as one small
// scene (rise + fade + a hair of scale) instead of snapping into view.
// ---------------------------------------------------------------------
export function RevealSection({ children, className = "", delay = 0, y = 56, ...rest }) {
  return (
    <motion.div
      initial={{ opacity: 0, y, scale: 0.98 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.85, delay, ease: EASE }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

// ---------------------------------------------------------------------
// 3) STAGGER ANIMATION
// Drop staggerContainer on a grid's parent and staggerItem on each
// card — children reveal one after another instead of all at once.
// ---------------------------------------------------------------------
export const staggerContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.22, delayChildren: 0.1 } },
};

export const staggerItem = {
  hidden: { opacity: 0, y: 46, scale: 0.94, rotate: -1.5 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    rotate: 0,
    transition: { duration: 0.65, ease: EASE },
  },
};

// ---------------------------------------------------------------------
// 4) TEXT ANIMATIONS
// Reveals a heading word-by-word (or letter-by-letter) with a stagger.
// ---------------------------------------------------------------------
export function SplitText({ text, by = "word", className = "", delay = 0, stagger = 0.05, as = "span" }) {
  const pieces = by === "letter" ? text.split("") : text.split(" ");
  const Tag = motion[as] || motion.span;
  return (
    <Tag className={className}>
      {pieces.map((piece, i) => (
        <motion.span
          key={`${piece}-${i}`}
          className="inline-block"
          initial={{ opacity: 0, y: "0.65em" }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.8 }}
          transition={{ duration: 0.55, delay: delay + i * stagger, ease: EASE }}
        >
          {piece === " " ? "\u00A0" : piece}
          {by === "word" && i !== pieces.length - 1 ? "\u00A0" : ""}
        </motion.span>
      ))}
    </Tag>
  );
}

// ---------------------------------------------------------------------
// 5) MAGNETIC BUTTONS
// Wraps any element; on hover it leans toward the cursor within a
// small radius, then springs back on leave.
// ---------------------------------------------------------------------
export function MagneticButton({ children, strength = 0.35, className = "" }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 200, damping: 15, mass: 0.4 });
  const springY = useSpring(y, { stiffness: 200, damping: 15, mass: 0.4 });

  const handleMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    x.set((e.clientX - (rect.left + rect.width / 2)) * strength);
    y.set((e.clientY - (rect.top + rect.height / 2)) * strength);
  };
  const handleLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={{ x: springX, y: springY }}
      className={`inline-block ${className}`}
    >
      {children}
    </motion.div>
  );
}

// ---------------------------------------------------------------------
// 6) CURSOR EFFECTS
// A small dot + trailing ring follow the pointer; the ring grows over
// anything tagged data-cursor="hover". Disabled on touch devices.
// Mount once near the root (we do it in Navbar).
// ---------------------------------------------------------------------
export function CustomCursor() {
  const [enabled, setEnabled] = useState(false);
  const [visible, setVisible] = useState(false);
  const [hovering, setHovering] = useState(false);
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const ringX = useSpring(x, { stiffness: 300, damping: 28 });
  const ringY = useSpring(y, { stiffness: 300, damping: 28 });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia("(pointer: fine)").matches) return;
    setEnabled(true);
    document.body.style.cursor = "none";

    const move = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
      if (!visible) setVisible(true);
      setHovering(!!e.target.closest?.("[data-cursor='hover']"));
    };
    const hide = () => setVisible(false);

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseleave", hide);
    return () => {
      document.body.style.cursor = "";
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseleave", hide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!enabled) return null;

  return (
    <>
      <motion.div
        className="fixed top-0 left-0 rounded-full pointer-events-none z-[999]"
        style={{ x, y, width: 8, height: 8, marginLeft: -4, marginTop: -4, backgroundColor: "#E63946" }}
        animate={{ opacity: visible ? 1 : 0 }}
        transition={{ duration: 0.2 }}
      />
      <motion.div
        className="fixed top-0 left-0 rounded-full border pointer-events-none z-[998]"
        style={{ x: ringX, y: ringY, borderColor: "rgba(230,57,70,0.55)" }}
        animate={{
          opacity: visible ? 1 : 0,
          width: hovering ? 56 : 30,
          height: hovering ? 56 : 30,
          marginLeft: hovering ? -28 : -15,
          marginTop: hovering ? -28 : -15,
          backgroundColor: hovering ? "rgba(230,57,70,0.08)" : "rgba(230,57,70,0)",
        }}
        transition={{ duration: 0.25, ease: "easeOut" }}
      />
    </>
  );
}
// ---------------------------------------------------------------------
// 9) HORIZONTAL SCROLL SECTION  [NEW]
// Pins the section for one vertical "pass" and translates an inner
// track sideways as the page scrolls. Desktop + motion-ok only —
// mobile and prefers-reduced-motion get the plain vertical stack the
// markup already renders, no JS branching needed for that part.
// ---------------------------------------------------------------------
export function HorizontalScrollSection({
  items,
  renderCard,
  className = "",
  cardWidth = 380,
  gap = 24,
  accent = "#E63946",
}) {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const mm = gsap.matchMedia();

    mm.add(
      { isDesktop: "(min-width: 768px) and (prefers-reduced-motion: no-preference)" },
      (context) => {
        if (!context.conditions.isDesktop) return; // mobile/reduced-motion: CSS fallback below handles it

        const track = trackRef.current;
        const totalScroll = track.scrollWidth - track.parentElement.offsetWidth;
        if (totalScroll <= 0) return;

        const tween = gsap.to(track, {
          x: -totalScroll,
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top top",
            end: () => `+=${totalScroll}`,
            scrub: 0.6,
            pin: true,
            anticipatePin: 1,
            invalidateOnRefresh: true,
            onUpdate: (self) => setProgress(self.progress),
          },
        });

        // returning a cleanup fn here lets gsap.matchMedia kill just
        // this context's ScrollTrigger when the media query stops matching
        return () => tween.scrollTrigger?.kill();
      }
    );

    return () => mm.revert(); // kills ScrollTriggers + resets track transform on unmount
  }, [items]);

  return (
    <div ref={sectionRef} className={`relative overflow-hidden ${className}`}>
      <style>{`
        .hscroll-card { width: 100%; }
        @media (min-width: 768px) {
          .hscroll-track { flex-direction: row; flex-wrap: nowrap; }
          .hscroll-card { width: ${cardWidth}px; flex: 0 0 ${cardWidth}px; }
        }
      `}</style>

      <div
        ref={trackRef}
        className="hscroll-track flex flex-col will-change-transform"
        style={{ gap }}
      >
        {items.map((item, i) => (
          <div key={item.id ?? i} className="hscroll-card">
            {renderCard(item, i)}
          </div>
        ))}
      </div>

      {/* progress indicator, desktop only */}
      <div className="hidden md:block absolute left-6 right-6 bottom-8 h-[2px] rounded-full bg-white/10 overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ backgroundColor: accent, scaleX: progress, transformOrigin: "left" }}
        />
      </div>
    </div>
  );
}
// ---------------------------------------------------------------------
// 7) PAGE LOAD ANIMATION
// A brief branded intro shown once on first mount, so the site "opens"
// rather than the hero simply appearing. Mount once near the root.
// ---------------------------------------------------------------------
export function PageLoader({ logo }) {
  const [show, setShow] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setShow(false), 850);
    return () => clearTimeout(t);
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.5, ease: "easeInOut" } }}
          className="fixed inset-0 z-[1000] flex items-center justify-center"
          style={{ backgroundColor: "#1A0E10" }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.04 }}
            transition={{ duration: 0.5, ease: EASE }}
          >
            <img src={logo} alt="" className="h-10 w-auto brightness-0 invert" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------
// 8) PARALLAX MOTION
// Ties a y-offset to how far a target element has scrolled through the
// viewport, for background layers that should drift at a different
// speed than the foreground content sitting on top of them.
// ---------------------------------------------------------------------
export function useParallax(ref, distance = 80) {
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  return useTransform(scrollYProgress, [0, 1], [-distance, distance]);
}